import { TimeLogApi, ToastStateHandler } from '../../../api';
import { ICallback } from '../../../commonTypes/hook.types';
import { TimeLogCreateDto } from '../../../commonTypes/timelog.types';
import { useContainer } from '../../useContainer';
import { useTranslation } from '../../useTranslation';
import { getUserMessage, normalizeError } from '../../../api/utils/ErrorHandler';
import { ErrorCodes } from '../../../api/utils/errorCodes';
import { useSelector } from 'react-redux';
import { selectActingUserSub } from '../../../store/slices/time-tracking.slice';
import { selectAuth } from '../../../store/slices/auth.slice';
import { checkIfAdminOrOwner } from '../../../api/utils/roleUtils';
import { useFeatureFlag } from '../../featureFlags/useFeatureFlags';

const useHttpPostTimeLog = () => {
    const timeLogApi: TimeLogApi = useContainer(TimeLogApi);
    const toastHandler = useContainer(ToastStateHandler);
    const { t } = useTranslation('newNotifications');
    const actingUserSub = useSelector(selectActingUserSub);
    const authUser = useSelector(selectAuth);
    const allowLogForOthers = useFeatureFlag('TIMELOG_ALLOW_LOG_FOR_OTHERS');

    const exec = (body: TimeLogCreateDto, callback: ICallback) => {
        const canActOnBehalf = checkIfAdminOrOwner(authUser.role.name) && allowLogForOthers;
        const payload: TimeLogCreateDto =
            canActOnBehalf && actingUserSub && actingUserSub !== authUser.userSub ? { ...body, userSub: actingUserSub } : body;

        const { exec, abort } = timeLogApi.create({
            body: payload,
            onSuccess: res => {
                toastHandler.push('success', t('timelog.createTimelogTitle'), t('timelog.createTimelogSuccess'));
                if (callback.onSuccess) callback.onSuccess(res.body);
            },
            onError: error => {
                const normalizedError = normalizeError(error);
                const errorMessage =
                    normalizedError.errorCode === ErrorCodes.USER_NOT_ASSIGNED_TO_PROJECT_ON_DATE
                        ? t('timelog.notAssignedForSelectedDate')
                        : getUserMessage(normalizedError, t('timelog.createTimelogError'));
                toastHandler.push('error', t('timelog.createTimelogTitle'), errorMessage);
                if (callback.onError) callback.onError(error);
            },
        });
        exec();
        return abort;
    };

    return { exec };
};

export default useHttpPostTimeLog;
